import cx from 'classnames';
import { Editor } from 'components/Editor';
import Constants from 'config/Constants';
import { documentApi } from 'features/documents/documents';
import { useFormikContext } from 'formik';
import { useGetTinyConfig } from 'hooks/useGetTinyConfig';
import _ from 'lodash';
import { useGetBaseVariant } from 'pages/documents_v2/hooks/useGetBaseVariant';
import { useGetDocumentTagNames } from 'pages/documents_v2/hooks/useGetDocumentTagNames';
import { useGetSections } from 'pages/documents_v2/hooks/useGetSections';
import { RenderedTextContent } from 'pages/documents_v2/views/edit_area/blocks/RenderedTextContent';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import striptags from 'striptags';

import EditorHelper from '../../../global/EditorHelper';
import HelperFunctions from '../../../global/HelperFunctions';
import { useGetDocument } from '../../hooks/useGetDocument';

export function TinymceEditor({ block, baseBlock, index, searchTerm, selectedSearchBlock, dirty = false }) {
    const { t } = useTranslation();
    const tags = useGetDocumentTagNames();
    const [showEditor, setShowEditor] = useState(false);
    const [warningDismissed, setWarningDismissed] = useState(false);
    const { values } = useFormikContext();

    const latestContent = block.content || block.latestContent || '';

    const [initialValue] = useState(() => latestContent);

    const changes = values.changes ?? [];
    const blockHasChanges = changes.some((change) => change.blockId === block.key);
    const canEdit = blockHasChanges === false;
    const hasLinkedBlocks = Object.hasOwn(block, 'linkedBlocks') && block.linkedBlocks.length > 0;

    const initialValuesPrepared = useMemo(() => {
        if (initialValue === undefined) {
            return '';
        }

        return initialValue.replaceAll('‘', '&lsquo;').replaceAll('’', '&rsquo;');
    }, [initialValue]);

    return (
        <div
            className={cx('editor-wrapper', {
                'cursor-pointer': !showEditor,
                active: showEditor,
                dirty: dirty || blockHasChanges,
            })}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (!showEditor && canEdit) {
                    if (hasLinkedBlocks && !warningDismissed) {
                        HelperFunctions.confirmModal(
                            t('documents:documents.message.blockIsLinked'),
                            t('global:btn.edit'),
                            t('global:btn.cancel'),
                        ).then(() => {
                            setWarningDismissed(true);
                            setShowEditor(true);
                        });
                    } else {
                        setShowEditor(true);
                    }
                }
            }}
        >
            <TinymceEditorInner
                block={block}
                baseBlock={baseBlock}
                tags={tags}
                initialValue={initialValuesPrepared}
                showEditor={showEditor}
                setShowEditor={setShowEditor}
                index={index}
                searchTerm={searchTerm}
                selectedSearchBlock={selectedSearchBlock}
                dirty={dirty}
                canEdit={canEdit}
            />
        </div>
    );
}

/**
 * @param block
 * @param baseBlock
 * @param {string[]} tags
 * @param initialValue
 * @param showEditor
 * @param setShowEditor
 * @param index
 * @param searchTerm
 * @param selectedSearchBlock
 * @param dirty
 * @param canEdit
 */
function TinymceEditorInner({
    block,
    baseBlock,
    tags,
    initialValue,
    showEditor,
    setShowEditor,
    index,
    searchTerm,
    selectedSearchBlock,
    dirty = false,
    canEdit = true,
}) {
    const { t } = useTranslation();
    const [editorLoaded, setEditorLoaded] = useState(false);
    const [options, setOptions] = useState(false);
    const editorRef = useRef(null);
    const [getAreaBlocks] = documentApi.useLazyGetAreaBlocksQuery();
    const tinyConfig = useGetTinyConfig();
    const { updateBlock, setSelectedBlockId, setSelectedPart } = useContext(EditAreaContext);

    const document = useGetDocument();
    const sections = useGetSections(document?.id, true);
    const baseVariant = useGetBaseVariant();

    const getAreas = useCallback(
        (sectionId) => {
            if (!sectionId) {
                return [];
            }

            const sectionIdAsInt = parseInt(sectionId);
            const section = sections.find((section) => section.id === sectionIdAsInt);

            if (!section) {
                return [];
            }

            return section.areas
                .filter((area) => area.deletedAt === null)
                .map((area) => {
                    const headingLevel = parseInt(area.exportProperties?.headingLevel ?? 2);
                    let prefix = '';

                    if (headingLevel > 2) {
                        for (let i = 0; i < headingLevel - 2; i++) {
                            prefix += '-- ';
                        }
                    }

                    return {
                        value: `${area.id}`,
                        text: prefix + area.title,
                    };
                });
        },
        [sections],
    );

    const createTag = useCallback(
        (data) => {
            let blocks = [];
            const blockKeys = Object.keys(data).filter((key) => key.startsWith('block-'));

            if (blockKeys.length > 0) {
                blockKeys.forEach((blockKey) => {
                    if (data[blockKey] === true) {
                        blocks.push(parseInt(blockKey.split('-')[1]));
                    }
                });
            }

            if (blocks.length > 0) {
                return `[Ref id=${blocks.join('|')} areaId=${parseInt(data.area)} type=block document=${document.id}]`;
            }

            if (!_.isEmpty(data.area)) {
                return `[Ref id=${parseInt(data.area)} type=area document=${document.id}]`;
            } else if (!_.isEmpty(data.section)) {
                return `[Ref id=${parseInt(data.section)} type=section document=${document.id}]`;
            }

            return undefined;
        },
        [document],
    );

    const getRefBodyItems = useCallback(
        async (data) => {
            let items = [];

            items.push({
                type: 'selectbox',
                name: 'section',
                label: 'Hoofdstuk*',
                items: [
                    {
                        value: '',
                        text: 'Selecteer hoofdstuk...',
                    },
                ].concat(
                    sections
                        .filter((section) => section.deletedAt === null)
                        .map((section) => ({
                            value: `${section.id}`,
                            text: section.title,
                        })),
                ),
            });

            items.push({
                type: 'selectbox',
                name: 'area',
                label: 'Artikel (optioneel)',
                items: [
                    {
                        value: '',
                        text: 'Selecteer artikel...',
                    },
                ].concat(getAreas(data?.section)),
                enabled: !_.isEmpty(data?.section),
            });

            if (!_.isEmpty(data?.area)) {
                const areaId = parseInt(data.area);

                const results = await getAreaBlocks({ areaId, variantId: baseVariant.id }, true);
                const areaBlocks = results?.data?.areaBlocks ?? [];

                const blocksWithNumbering = areaBlocks.filter((block) => {
                    const blockPrefixSetting = block.properties?.blockPrefixSetting;
                    const blockPrefix = block.properties?.blockPrefix;

                    return blockPrefixSetting === 'auto' || !_.isEmpty(blockPrefix);
                });

                if (blocksWithNumbering.length > 0) {
                    items.push({
                        type: 'label',
                        label: 'Blokken (optioneel)',
                        for: 'blocks',
                        items: [
                            {
                                type: 'panel',
                                items: blocksWithNumbering.map((block) => {
                                    const blockName = block.properties?.name;
                                    let contentPreview = striptags(block.latestContent);
                                    let partLength = 110;

                                    if (!_.isEmpty(blockName)) {
                                        partLength = partLength - blockName.length;

                                        if (partLength < 20) {
                                            partLength = 30;
                                        }
                                    }

                                    if (contentPreview.length > partLength + 5) {
                                        const startStr = contentPreview.substring(0, partLength / 2);
                                        const endStr = contentPreview.substring(contentPreview.length - partLength / 2);

                                        contentPreview = startStr.trim() + ' ... ' + endStr.trim();
                                    }

                                    let label = '';

                                    if (!_.isEmpty(blockName)) {
                                        label = `[${blockName}] `;
                                    }

                                    label = label + contentPreview;

                                    return {
                                        type: 'checkbox',
                                        name: `block-${block.id}`,
                                        label,
                                    };
                                }),
                            },
                        ],
                    });
                }
            }

            return items;
        },
        [sections, getAreaBlocks, baseVariant, getAreas],
    );

    const refDialogConfig = useCallback(
        async function buildRefDialogConfig(editor, data) {
            editor.focus();

            return {
                title: t('global:editor.btn.crossRef'),
                size: 'medium',
                body: {
                    type: 'panel',
                    items: await getRefBodyItems(data),
                },
                onChange: async (dialogApi) => {
                    dialogApi.redial(await buildRefDialogConfig(editor, dialogApi.getData()));
                },
                buttons: [
                    {
                        type: 'cancel',
                        name: 'closeButton',
                        text: 'Cancel',
                    },
                    {
                        type: 'submit',
                        name: 'submitButton',
                        text: 'OK',
                        buttonType: 'primary',
                        enabled: !_.isEmpty(data?.section),
                    },
                ],
                initialData: {
                    section: data?.section ?? '',
                    area: data?.area ?? '',
                },
                onSubmit: (api) => {
                    const tag = createTag(api.getData());

                    if (!tag) {
                        api.close();
                    }

                    editor.selection.setContent(tag);
                    api.close();
                },
            };
        },
        [t, getRefBodyItems, createTag],
    );

    const getTinyMceOptions = useCallback(async () => {
        // define block type specific config sets or configs that need dynamic content/functions
        return {
            setup: (editor) => {
                if (tags) {
                    editor.ui.registry.addMenuButton('tags', {
                        text: t('global:editor.btn.tags'),
                        fetch: function (callback) {
                            const items = EditorHelper.generateTagMenu(tags, editor);
                            callback(items);
                        },
                    });
                }
                if (document.type !== Constants.documentType.library) {
                    editor.ui.registry.addButton('ref', {
                        text: t('global:editor.btn.crossRef'),
                        onAction: async () => editor.windowManager.open(await refDialogConfig(editor)),
                    });
                }
                editor.ui.registry.addButton('startNumber', {
                    text: t('global:editor.btn.startListWith'),
                    onAction: function () {
                        const parent = editor.dom.getParent(editor.selection.getNode(), 'ol');

                        if (parent !== null) {
                            editor.windowManager.open({
                                title: t('global:editor.title.startNumber'),
                                body: {
                                    type: 'panel',
                                    items: [
                                        {
                                            type: 'input',
                                            inputMode: 'text',
                                            name: 'startNumber',
                                            label: t('global:editor.title.startNumber'),
                                        },
                                    ],
                                },
                                buttons: [
                                    {
                                        type: 'submit',
                                        text: 'OK',
                                    },
                                ],
                                onSubmit: function (dialog) {
                                    const data = dialog.getData();
                                    const number = HelperFunctions.filterNumbers(data.startNumber);
                                    parent.setAttribute('start', number);

                                    dialog.close();
                                },
                            });
                        }
                    },
                    onSetup: (buttonApi) => {
                        // Make button disabled when no "ol" element is selected
                        editor.on('NodeChange', function (e) {
                            const isOrderedList =
                                e.element.nodeName.toLowerCase() === 'li' &&
                                e.parents[1] &&
                                e.parents[1].nodeName.toLowerCase() === 'ol';
                            buttonApi.setEnabled(isOrderedList);
                        });
                    },
                });
            },
        };
    }, [tags, t, document, refDialogConfig]);

    const readOnly = !showEditor || !options || !editorLoaded || !canEdit;
    const latestContent = block.content || block.latestContent || '';

    const newTextBlockPlaceholder = `<p className='newTextBlockPlaceholder'>${t(
        'documents:document.navbar.main.editor.left.blocks.editBlockText',
    )}...</p>`;

    useEffect(() => {
        if (showEditor && !options && document) {
            getTinyMceOptions().then((tinyMceOptions) => {
                setOptions({
                    ...tinyMceOptions,
                    ...tinyConfig,
                });
            });
        }
    }, [showEditor, document, options, getTinyMceOptions, tinyConfig]);

    useEffect(() => {
        if (editorLoaded && showEditor && editorRef.current) {
            // Move cursor to end of content
            editorRef.current.selection.select(editorRef.current.getBody(), true);
            editorRef.current.selection.collapse(false);
        }
    }, [showEditor, editorLoaded]);

    const latestContentPrepared = useMemo(() => {
        if (!document) {
            return latestContent;
        }

        if (latestContent === '') {
            return newTextBlockPlaceholder;
        }

        return HelperFunctions.prepareTags(latestContent, document, true, true, sections, tags);
    }, [document, latestContent, tags, sections, newTextBlockPlaceholder]);

    const highlightedContent = useMemo(() => {
        if (!searchTerm || showEditor) {
            return latestContentPrepared; // No highlighting needed
        }

        const classes = cx('highlight-search', {
            'highlight-search-selected': selectedSearchBlock?.key === block.key,
        });

        return latestContentPrepared.replace(
            new RegExp(searchTerm, 'gi'),
            (match) => `<mark className="${classes}">${match}</mark>`,
        );
    }, [searchTerm, showEditor, selectedSearchBlock?.key, block.key, latestContentPrepared]);

    return (
        <>
            <div
                className={cx('tiny-mce-content-body', {
                    'd-none': !readOnly,
                })}
            >
                <RenderedTextContent
                    block={block}
                    baseBlock={baseBlock}
                    highlightedContent={highlightedContent}
                    index={index}
                    dirty={dirty}
                />
            </div>
            <div
                className={cx('tiny-mce-content-body ', {
                    'd-none': readOnly,
                })}
            >
                {!!options && (
                    <Editor
                        id={`jsTinymceEditor${block.key}`}
                        init={options}
                        initialValue={initialValue}
                        value={latestContent}
                        onEditorChange={(content) => updateBlock(content.replace(/\r?\n|\r/g, ''), index)}
                        onBlur={() => {
                            setShowEditor(false);
                            setSelectedBlockId(undefined);
                            setSelectedPart(undefined);
                        }}
                        onFocus={() => {
                            setSelectedBlockId(block.key);
                        }}
                        onInit={(event, editor) => {
                            setEditorLoaded(true);
                            editorRef.current = editor;
                        }}
                    />
                )}
            </div>
        </>
    );
}
